import React from 'react';
import { MessageCircle } from 'lucide-react';

const WHATSAPP_NUMBER_SA = '+27783891391';
const WHATSAPP_NUMBER_MZ = '+258846943840';

/**
 * Generate WhatsApp click-to-chat URL
 */
export const generateWhatsAppLink = (message = '', country = 'SA') => {
  const number = country === 'MZ' ? WHATSAPP_NUMBER_MZ : WHATSAPP_NUMBER_SA;
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${number.replace(/[^0-9]/g, '')}?text=${encodedMessage}`;
};

/**
 * Pre-defined message templates for different scenarios
 */
export const WhatsAppTemplates = {
  // General inquiries
  general: {
    en: "Hi! I'm visiting The Guy in SA website and would like more information.",
    pt: "Olá! Estou a visitar o site The Guy in SA e gostaria de mais informações."
  },
  
  // Quote related
  quoteStatus: (quoteId, lang = 'en') => 
    lang === 'pt' 
      ? `Olá! Gostaria de saber o estado da minha cotação #${quoteId}.`
      : `Hi! I'd like to check the status of my quote #${quoteId}.`,
  
  quoteQuestion: (quoteId, lang = 'en') => 
    lang === 'pt'
      ? `Olá! Tenho uma questão sobre a minha cotação #${quoteId}.`
      : `Hi! I have a question about my quote #${quoteId}.`,
  
  // Order related
  orderStatus: (orderId, lang = 'en') => 
    lang === 'pt'
      ? `Olá! Gostaria de verificar o estado da minha encomenda #${orderId}.`
      : `Hi! I'd like to check the status of my order #${orderId}.`,
  
  orderIssue: (orderId, lang = 'en') => 
    lang === 'pt'
      ? `Olá! Tenho um problema com a minha encomenda #${orderId}. Pode ajudar?`
      : `Hi! I have an issue with my order #${orderId}. Can you help?`,
  
  // Personal Shopper
  shopperStatus: (serviceId, lang = 'en') => 
    lang === 'pt'
      ? `Olá! Gostaria de uma atualização sobre o meu pedido de Personal Shopper #${serviceId}.`
      : `Hi! I'd like an update on my Personal Shopper request #${serviceId}.`,
  
  shopperNew: {
    en: "Hi! I'm interested in your Personal Shopper service. Can you help me find products in South Africa?",
    pt: "Olá! Estou interessado no serviço de Personal Shopper. Pode ajudar-me a encontrar produtos na África do Sul?"
  },
  
  // Pickup & Delivery
  pickupStatus: (pickupId, lang = 'en') => 
    lang === 'pt'
      ? `Olá! Qual é o estado do meu pickup #${pickupId}?`
      : `Hi! What's the status of my pickup #${pickupId}?`,
  
  pickupNew: {
    en: "Hi! I need a pickup and delivery service. Can you provide a quote?",
    pt: "Olá! Preciso de um serviço de recolha e entrega. Pode fornecer uma cotação?"
  },
  
  // Company Registration
  companyStatus: (regId, lang = 'en') => 
    lang === 'pt'
      ? `Olá! Gostaria de uma atualização sobre o registo da minha empresa #${regId}.`
      : `Hi! I'd like an update on my company registration #${regId}.`,
  
  companyNew: {
    en: "Hi! I'm interested in registering a company. Can you help?",
    pt: "Olá! Estou interessado em registar uma empresa. Pode ajudar?"
  },
  
  // New quote request
  newQuote: {
    en: "Hi! I'd like to request a quote for products from South Africa.",
    pt: "Olá! Gostaria de pedir uma cotação para produtos da África do Sul."
  },
  
  // Supplier inquiry
  supplierInquiry: {
    en: "Hi! I'm a supplier and would like to join The Guy in SA platform.",
    pt: "Olá! Sou um fornecedor e gostaria de aderir à plataforma The Guy in SA."
  }
};

/**
 * Basic WhatsApp floating button
 */
export const WhatsAppButton = ({ country = 'SA', message = '', language = 'en' }) => {
  const defaultMessage = message || WhatsAppTemplates.general[language];
  const whatsappUrl = generateWhatsAppLink(defaultMessage, country);

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-float"
      data-testid="whatsapp-button"
      aria-label="Chat on WhatsApp"
    >
      <MessageCircle className="w-8 h-8 text-white" />
    </a>
  );
};

/**
 * WhatsApp link component
 */
export const WhatsAppLink = ({ country = 'SA', message = '', children, className = '' }) => {
  const whatsappUrl = generateWhatsAppLink(message, country);

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      data-testid="whatsapp-link"
    >
      {children}
    </a>
  );
};

/**
 * WhatsApp action button with icon
 */
export const WhatsAppActionButton = ({ 
  message, 
  country = 'SA',
  variant = 'default',
  size = 'default',
  className = '',
  children,
  language = 'en'
}) => {
  const finalMessage = typeof message === 'object' ? message[language] : message;
  const whatsappUrl = generateWhatsAppLink(finalMessage, country);
  
  const baseStyles = "inline-flex items-center gap-2 font-medium transition-all duration-200 rounded-lg";
  
  const variants = {
    default: "bg-[#25D366] hover:bg-[#128C7E] text-white",
    outline: "border-2 border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white",
    ghost: "text-[#25D366] hover:bg-[#25D366]/10",
    subtle: "bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20"
  };
  
  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    default: "px-4 py-2",
    lg: "px-6 py-3 text-lg"
  };
  
  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      data-testid="whatsapp-action-btn"
    >
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      {children}
    </a>
  );
};

/**
 * WhatsApp status inquiry button - shows contextual button for checking status
 */
export const WhatsAppStatusButton = ({
  type, // 'quote' | 'order' | 'service' | 'pickup' | 'company'
  id,
  language = 'en',
  country = 'SA',
  size = 'sm'
}) => {
  const messages = {
    quote: WhatsAppTemplates.quoteStatus(id, language),
    order: WhatsAppTemplates.orderStatus(id, language),
    service: WhatsAppTemplates.shopperStatus(id, language),
    pickup: WhatsAppTemplates.pickupStatus(id, language),
    company: WhatsAppTemplates.companyStatus(id, language)
  };
  
  const labels = {
    en: 'Ask on WhatsApp',
    pt: 'Perguntar no WhatsApp'
  };
  
  return (
    <WhatsAppActionButton
      message={messages[type]}
      country={country}
      variant="subtle"
      size={size}
    >
      {labels[language]}
    </WhatsAppActionButton>
  );
};

export default WhatsAppButton;
